import React from 'react';
import { Github } from 'lucide-react';

import { Button } from '@/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/ui/dialog';
import { useAuth } from '@/app/providers/AuthContext';

type ButtonVariant = 'default' | 'link' | 'destructive' | 'outline' | 'secondary' | 'ghost';

interface SupabaseLoginButtonProps {
  buttonText?: string;
  showModal?: boolean;
  className?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: React.ReactNode;
}

type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

const SupabaseLoginButton: React.FC<SupabaseLoginButtonProps> = ({
  buttonText = 'Sign up for free',
  className = '',
  showModal = true,
  variant = 'default',
  size = 'default',
  children
}) => {
  const { login } = useAuth();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      await login();
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const buttonContent = children || buttonText;

  if (showModal) {
    return (
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogTrigger asChild>
          <Button className={className} variant={variant} size={size}>
            {buttonContent}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-center">Sign up for an account</DialogTitle>
            <DialogDescription className="text-center">
              Create an account to track your usage and get access to premium features.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Button
              className="w-full"
              size="lg"
              onClick={handleLogin}
              disabled={isLoading}
              variant={variant}
            >
              {isLoading ? (
                'Redirecting...'
              ) : (
                <>
                  <Github className="mr-2 h-4 w-4" /> Sign up with GitHub
                </>
              )}
            </Button>
            <p className="px-8 text-center text-sm text-muted-foreground mt-4">
              We use GitHub authentication to securely access your repositories.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Button
      className={className}
      onClick={handleLogin}
      disabled={isLoading}
      variant={variant}
      size={size}
    >
      {isLoading ? 'Redirecting...' : buttonContent}
    </Button>
  );
};

export default SupabaseLoginButton;
